import dayjs, { Dayjs } from "dayjs";
import WorkDay from "../Types/WorkDay";
import WorkPeriod from "../Types/WorkPeriod";
import WorkerData from "../Types/WorkerData";
import { CountedState } from "../Types/Enums/CountedState";
import { v4 as uuidv4 } from "uuid";

export default class WorkDayService {
    public static cloneWorker(worker: WorkerData): WorkerData {
        const clone = new WorkerData(
            worker.Guid,
            worker.UserId,
            worker.FirstName,
            worker.LastName,
            worker.Debt,
            worker.RoundedTip,
            worker.Permission,
            worker.Function,
            worker.CashTip,
            worker.PinTip,
        );

        clone.TipStatus = worker.TipStatus;
        clone.Username = worker.Username;
        clone.PreviousTipsReceived = worker.PreviousTipsReceived;

        return clone;
    }

    public static cloneWorkPeriod(period: WorkPeriod): WorkPeriod {
        return new WorkPeriod(
            period.Id,
            period.Name,
            period.Date,
            period.TipPin,
            period.TipCash,
            (period.Workers ?? []).map((worker) => WorkDayService.cloneWorker(worker)),
        );
    }

    public static getDisplayPeriods(workDay: WorkDay | null): WorkPeriod[] {
        if (workDay == null) return [];
        if (workDay.IsClosed) return [];
        if ((workDay.WorkPeriods ?? []).length > 0) {
            return workDay.WorkPeriods.map((period) => WorkDayService.cloneWorkPeriod(period));
        }

        const hasDayData =
            (workDay.Workers?.length ?? 0) > 0 ||
            (workDay.TipCash ?? 0) > 0 ||
            (workDay.TipPin ?? 0) > 0 ||
            workDay.IsClosed;

        if (!hasDayData) return [];

        return [
            new WorkPeriod(
                workDay.Id || uuidv4(),
                "Work period 1",
                workDay.Date,
                workDay.TipPin ?? 0,
                workDay.TipCash ?? 0,
                (workDay.Workers ?? []).map((worker) => WorkDayService.cloneWorker(worker)),
            ),
        ];
    }

    public static hasWorkDayData(workDay: WorkDay | undefined | null): boolean {
        if (workDay == null) return false;
        if (workDay.IsClosed) return true;
        if ((workDay.TipPin ?? 0) > 0 || (workDay.TipCash ?? 0) > 0 || (workDay.Workers?.length ?? 0) > 0) return true;

        return (workDay.WorkPeriods ?? []).some((period) =>
            (period.TipPin ?? 0) > 0 ||
            (period.TipCash ?? 0) > 0 ||
            (period.Workers?.length ?? 0) > 0
        );
    }

    public static createEmptyWorkPeriod(date: string): WorkPeriod {
        return new WorkPeriod(uuidv4(), "", date, 0, 0, []);
    }

    public static buildWorkDay(source: WorkDay | Dayjs, workPeriods: WorkPeriod[]): WorkDay {
        const clonedPeriods = workPeriods.map((period) => WorkDayService.cloneWorkPeriod(period));
        const workerMap = new Map<string, WorkerData>();

        clonedPeriods.forEach((period) => {
            (period.Workers ?? []).forEach((worker) => {
                if (!workerMap.has(worker.Guid)) {
                    workerMap.set(worker.Guid, WorkDayService.cloneWorker(worker));
                }
            });
        });

        const cashTip = clonedPeriods.reduce((sum, period) => sum + (period.TipCash ?? 0), 0);
        const pinTip = clonedPeriods.reduce((sum, period) => sum + (period.TipPin ?? 0), 0);
        const date = dayjs.isDayjs(source) ? source.format("YYYY-MM-DD") : source.Date;

        if (dayjs.isDayjs(source)) {
            return new WorkDay(
                "",
                date,
                pinTip,
                cashTip,
                false,
                CountedState.NotCounted,
                [],
                Array.from(workerMap.values()),
                clonedPeriods,
            );
        }

        return new WorkDay(
            source.Id,
            date,
            pinTip,
            cashTip,
            clonedPeriods.length === 0 ? source.IsClosed : false,
            source.CountedState,
            [...(source.CountedBy ?? [])],
            Array.from(workerMap.values()),
            clonedPeriods,
        );
    }

    public static getWorkPeriodLabel(period: WorkPeriod, index: number): string {
        return period.Name?.trim() || `Work period ${index + 1}`;
    }
}
